import React from "react";
import { motion } from "framer-motion";
import {
  Rocket,
  MessageCircle,
  MapPin,
  PhoneOff,
  Database,
  TrendingUp,
} from "lucide-react";

const BENEFITS = [
  {
    icon: Rocket,
    title: "10-minute setup",
    body: "No technical knowledge needed. Open Ping, add staff, you're live.",
  },
  {
    icon: MessageCircle,
    title: "Works on WhatsApp",
    body: "No app for customers to download. They already have WhatsApp.",
  },
  {
    icon: MapPin,
    title: "Built for Service Businesses",
    body: "Whether you're a garage, salon, tailor, or dealership — Ping is designed around the way real service businesses operate and grow.",
  },
  {
    icon: PhoneOff,
    title: "AI That Works While You Sleep",
    body: "Ping's AI handles customer updates, follow-ups, and replies automatically — so your staff focus on the work, not the phone.",
  },
  {
    icon: Database,
    title: "Builds Your Marketing Engine",
    body: "Every customer is captured automatically and segmented intelligently — ready for targeted campaigns whenever you need them.",
  },
  {
    icon: TrendingUp,
    title: "Smart Campaigns, Real Returns",
    body: "Run seasonal promotions and AI-powered marketing blasts to hundreds of customers in seconds. One returning customer and Ping has already paid for itself.",
  },
];

export const WhyPing = () => {
  return (
    <section
      id="why-ping"
      data-testid="why-ping-section"
      className="bg-white py-20 sm:py-28 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] ping-gradient-text mb-4">
            Why Ping
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-[3.5rem] lg:leading-[1.05] font-black tracking-tight text-[#1A1A2E]">
            Why businesses <span className="ping-gradient-text">choose Ping</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.title}
                data-testid={`why-ping-tile-${i + 1}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group bg-[#F9FAFB] border border-gray-200/70 rounded-2xl p-6 sm:p-7 hover:bg-white hover:border-[#7B5EA7]/30 hover:shadow-xl hover:shadow-gray-200/60 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl ping-gradient flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <Icon size={20} className="text-white" strokeWidth={2.2} />
                </div>
                <h3 className="text-lg font-bold text-[#1A1A2E] tracking-tight mb-2">
                  {b.title}
                </h3>
                <p className="text-[14.5px] text-gray-600 leading-relaxed">
                  {b.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyPing;
